import { FormControl, FormHelperText, Stack, TextField } from '@mui/material';
import { DatePicker, TimePicker, TimePickerFieldProps, useSplitFieldProps } from '@mui/x-date-pickers';
import { useReferredState } from 'hooks/useReferredState';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { useMprcTheme } from './MprcThemeProvider';
////////////////////////////////////////////////////////////////////////////

export type MprcDateTimePickerFormatType = "initialDate" | "endDate" | "default";

export type DatePositionChanged = "date" | "time" | "datetime";

export type MprcDateTimeSource = "typing" | "picker";

interface IProps {
    value: Date | null;
    format?: MprcDateTimePickerFormatType;
    margin?: MuiMargin;
    title?: string;
    dateLabel?: string;
    timeLabel?: string;
    placeholder?: string;
    fullWidth?: boolean;
    disabled?: boolean;
    disabledOnlyTime?: boolean;
    disablePast?: boolean;
    minDate?: Date;
    maxDate?: Date;
    width?: string | number;
    error?: boolean;
    helperText?: React.ReactNode;
    required?: boolean;
    shrinkLabel?: boolean;
    onChange: (date: Date | null, validate: boolean, position: DatePositionChanged, source: MprcDateTimeSource) => void;
    onBlur?: () => void;
    onTimeBlur?: () => void;
}

////////////////////////////////////////////////////////////////////////////

export function dateTimePickerConvertTime(value: Date | null, format: MprcDateTimePickerFormatType) {

    if (value != null) {
        var dt = moment(value).toDate();
        if ((format == "initialDate" && !(dt.getHours() === 0 && dt.getMinutes() === 0 && dt.getSeconds() == 0)) ||
            (format == "endDate" && !(dt.getHours() === 23 && dt.getMinutes() === 59 && dt.getSeconds() == 59)) ||
            format == "default") {
            return moment(value, "HH:mm:ss");
        }
    }

    return null;
}

export function formatDateTime(date: Date, time: Date | null, format: MprcDateTimePickerFormatType) {

    if (time === null) {
        if (format == "initialDate" || format == "default") {
            date.setHours(0);
            date.setMinutes(0);
            date.setSeconds(0);
        }
        else if (format == "endDate") {
            date.setHours(23);
            date.setMinutes(59);
            date.setSeconds(59);
        }
    }
    else {
        date.setHours(time.getHours());
        date.setMinutes(time.getMinutes());

        if (format == "endDate" && time.getHours() == 23 && time.getMinutes() == 59) {
            date.setSeconds(59);
        }
        else {
            date.setSeconds(0);
        }
    }

    return date;
}

////////////////////////////////////////////////////////////////////////////

const TimeField = (props: TimePickerFieldProps & { myPlaceholder: string; }) => {
    const { internalProps, forwardedProps } = useSplitFieldProps(props, 'time');
    return (
        <TextField
            {...forwardedProps}
            {...internalProps}
            placeholder={props.myPlaceholder}
        />
    );
};

////////////////////////////////////////////////////////////////////////////

export const MprcDateTimePicker: React.FC<IProps> = (props) => {

    const { shrinkLabel, fullWidth, margin, required, format = "default", value,
        minDate, maxDate, placeholder, disabled, disabledOnlyTime, disablePast, error, helperText, dateLabel, timeLabel, title, width = "140px"
        , onChange, onBlur, onTimeBlur } = props;
    const { theme } = useMprcTheme();

    const [date, setDate, refDate] = useReferredState<Moment | null>(value == null ? null : moment(value));
    const [time, setTime, refTime] = useReferredState<Moment | null>(dateTimePickerConvertTime(value, format));
    const [valid, setValid] = useState<boolean>();
    const [pickerIsOpen, setPickerIsOpen] = useState<boolean>(false);
    const [source, setSource] = useState<MprcDateTimeSource>("typing");

    ///
    const updateValue = (dtMoment: Moment | null, position: DatePositionChanged, changesSource: MprcDateTimeSource) => {
        const isValid = dtMoment === null || (dtMoment.isValid() && dtMoment.year() >= 1970);
        onChange(
            refDate.current == null ? null : formatDateTime(refDate.current.toDate(), refTime.current == null ? null : refTime.current.toDate(), format)
            , isValid
            , position
            , changesSource
        );
    };

    ///
    const handleUpdateDate = (dtMoment: Moment | null) => {
        const newSource = pickerIsOpen ? "picker" : "typing";
        setSource(newSource);
        if (dtMoment != null) {
            setDate(dtMoment);
        }
        else {
            setDate(null);
            setTime(null);
        }

        updateValue(dtMoment, "date", newSource);
    };

    ///
    const handleUpdateTime = (dtMoment: Moment | null) => {
        const newSource = pickerIsOpen ? "picker" : "typing";
        setSource(newSource);
        setTime(dtMoment);
        updateValue(dtMoment, "time", newSource);
    };

    ///
    useEffect(() => {
        var mDate = value == null ? null : moment(value);
        if (mDate === null || mDate.isValid()) {
            setDate(mDate);
            setTime(dateTimePickerConvertTime(value, format));
        }
    }, [value]);

    ///
    useEffect(() => {
        if (valid != undefined) {
            onChange(date == null ? null : formatDateTime(date.toDate(), time == null ? null : time.toDate(), format), valid, "datetime", source);
        }
    }, [valid]);

    ///
    return (
        <Stack direction="column" spacing={1}>
            <Stack spacing={2} direction="row" alignItems="end">
                <div>
                    <FormControl margin={margin} fullWidth={fullWidth}>
                        <DatePicker
                            label={dateLabel}
                            disabled={disabled}
                            openTo="day"
                            disablePast={disablePast}
                            minDate={minDate != undefined ? moment(minDate) : undefined}
                            maxDate={maxDate != undefined ? moment(maxDate) : undefined}
                            sx={{ "& .Mui-disabled .MuiPickersInputBase-sectionsContainer": { color: disabled ? "muted.main" : undefined } }}
                            slotProps={{
                                textField: {
                                    required: required,
                                    placeholder: placeholder,
                                    title: title,
                                    style: { width: fullWidth ? undefined : width },
                                    onBlur: onBlur,
                                    error: valid === false || error,
                                } as any,
                            }}
                            value={date}
                            onChange={e => handleUpdateDate(e)}
                            onError={(reason, v) => setValid(reason === null)}
                            onOpen={() => setPickerIsOpen(true)}
                            onClose={() => setPickerIsOpen(false)}
                        />
                    </FormControl>
                </div>
                <div>
                    <FormControl margin={margin} fullWidth={fullWidth}>
                        <TimePicker
                            enableAccessibleFieldDOMStructure={false}
                            label={timeLabel ?? ""}
                            disabled={disabledOnlyTime || disabled || date == null}
                            sx={{ "& .Mui-disabled .MuiPickersInputBase-sectionsContainer": { color: disabled ? "muted.main" : undefined } }}
                            value={time}
                            slots={{
                                textField: TimeField
                            }}
                            slotProps={
                                {
                                    textField: {
                                        required: required,
                                        myPlaceholder: format == "initialDate"
                                            ? theme.MprcDateTimePicker.firstTimeOfDayText
                                            : format == "endDate"
                                                ? theme.MprcDateTimePicker.endTimeOfDayText
                                                : undefined,
                                        title: title,
                                        style: { width: fullWidth ? undefined : width },
                                        InputLabelProps: shrinkLabel ? { shrink: shrinkLabel } : undefined,
                                        error: valid === false || error,
                                        variant: "standard",
                                        onBlur: onTimeBlur
                                    } as any
                                }
                            }
                            onChange={handleUpdateTime}
                            onOpen={() => setPickerIsOpen(true)}
                            onClose={() => setPickerIsOpen(false)}
                            onError={(reason, v) => setValid(reason === null)}
                        />
                    </FormControl>
                </div>
            </Stack>
            {helperText && error &&
                <FormHelperText error={error}>
                    {helperText}
                </FormHelperText>
            }
        </Stack >
    );
};
